// ── WHAT WE THINK THE CLAIM SHOULD PAY ───────────────────────────────────────
//
// Kevin, 2026-09-23: *"We will calculate what we think the claim should be and then see
// what actually arrives when the claim is processed and paid by AIP… I want to have an
// estimate as soon as we have all the data for production or acres for PP."*
//
// This module answers two questions and keeps them apart on purpose:
//
//   1. WHAT WOULD IT PAY, given the inputs.
//   2. DO WE ACTUALLY HAVE THOSE INPUTS YET (`estimateReadiness`).
//
// ⚠ THE SECOND IS THE ONE THAT MAKES THE FIRST WORTH FREEZING. An estimate built on a
// guessed price, a missing share or an acreage nobody reported is not an estimate, it is
// a number — and once it is written to `claim_estimates` it can never be corrected
// (sql/074 puts a BEFORE UPDATE trigger on that table that raises). So nothing here
// defaults an input. A missing figure makes the estimate UNAVAILABLE and says which one,
// which is [[feedback_no_guessing]] applied to the one surface where being wrong is
// permanent.
//
// ── SOURCES ──────────────────────────────────────────────────────────────────
// 7 CFR 457.8 (Common Crop Insurance Policy Basic Provisions) and the Prevented
// Planting Standards Handbook **FCIC-25370 (12-2025)**, effective CY2026. Every rule
// below cites its paragraph. Nothing here is back-solved from our own data: the
// cross-check (claimCrossCheck.js) is what compares this to the carrier's worksheet,
// and a model fitted to that worksheet could not disagree with it, which is the whole
// point of having both.


import { harvestShortfall, machineDisagreement, MACHINE_DISAGREE_PCT } from "./harvestRule.js";
import { coverageBasis } from "./claimCrops.js";

const num = (v) => (v == null || v === "" || !isFinite(Number(v)) ? null : Number(v));
const up = (s) => String(s == null ? "" : s).trim().toUpperCase();
// Money is compared and summed in integer cents. Two float additions of the same
// figures do not always agree, and this module's output is latched against a carrier
// payment to the dollar.
const cents = (d) => Math.round(d * 100);
const dollars = (c) => c / 100;

// ── PREVENT PLANT IS NOT ESTIMATED IN THIS FILE ──────────────────────────────
//
// `estimatePreventPlant` lived here until 2026-10-19 and was deleted, deliberately. Nothing
// imported it — App.jsx said so in a comment — and a second prevent-plant engine with no
// caller is how two engines come to disagree: it capped eligibility but never rolled, and
// it priced a missing price election at 100%. THE prevent-plant determination is
// `adjudicatePreventPlant` in ppAdjuster.js, priced through ppRate.js. Its checker
// assertions were removed with it, on purpose (tools/check-claim-estimate.js).
//
// ⚠ ONE SOURCED RULE IT HELD IS KEPT, BECAUSE IT EXISTS NOWHERE ELSE. A second crop
// planted on prevented acreage reduces the PP payment to 35% unless the double-crop
// history of §15(h) is met — 7 CFR 457.8 §15(f)(2); FCIC-25370 ¶75(2). The engine holds
// no second-crop input (the M13 file does not say which prevented acres were later
// planted to a second crop), so it does NOT apply this: it names "second crop" as not
// evaluated and leaves it for a person. The constant is the reference for that person.
export const SECOND_CROP_FACTOR = 0.35; // 7 CFR 457.8 §15(f)(2)

// ── PRODUCTION / REVENUE ─────────────────────────────────────────────────────
//
// ⚠ MOVED HERE FROM App.jsx, NOT COPIED. `computeHarvestClaim` has been the Claim
// Manager's engine since the screener shipped; the estimate that gets FROZEN has to be
// the same arithmetic the roster shows, or the tool compares the carrier against a
// number no screen ever displayed. This repo's record on a rule defined twice is
// unambiguous — two guarantee engines disagreed about one farm's forward-sell ceiling,
// three news handlers drifted twice — so App.jsx imports this one.

// RMA-basis net bushels from scale tickets: re-shrink each load's gross at shrinkPerPt
// %/pt above baseMoist (water only); fall back to elevator net when a load lacks
// gross/moisture.
export function rmaNetFromTickets(tickets, shrinkPerPt = 1.2, baseMoist = 13.5) {
  return (tickets || []).reduce((s, t) => {
    const g = Number(t.gross_bushels), m = Number(t.moisture);
    if (isFinite(g) && g > 0 && isFinite(m)) { const pts = Math.max(0, m - baseMoist); return s + g * (1 - (shrinkPerPt / 100) * pts); }
    return s + (Number(t.net_bushels) || 0);
  }, 0);
}

// ── THE ONE HARVEST CLAIM ENGINE ─────────────────────────────────────────────
//
// ⚠ UNTIL 2026-10-19 THERE WERE TWO. The roster called this function; ClaimCheckView (the
// single-farm drill-in) re-derived the same arithmetic inline — its own ticket re-shrink, its
// own guarantee price, its own indemnity — and the copies had already drifted three ways:
//   - the drill-in counted appraised bushels (when its toggle was on) and the roster never did,
//     so a chopped-corn farm read "under" on one screen and "no tickets" on the next;
//   - the roster's yield test was `/^YP|YIELD/`, which misses the `APH` plan (potatoes, dry
//     beans, onions, triticale…); the drill-in used claimBasis. Latent only because RMA
//     publishes no harvest price for those crops;
//   - only the drill-in turned a shortfall into dollars.
// Now every production SOURCE is handed in by name, each becomes one labelled line, and the
// total is the sum of the counted lines. Two screens cannot disagree about which bushels
// count, because neither of them adds anything up.
//
// `sources` keys — `tickets`, `appraisals` and `machine` are REQUIRED to be present (null is
// a deliberate "this source is not in play"; an ABSENT key is a caller that forgot one, and
// throws). A dropped options key is invisible to check-shared-rules, so it is refused here.
//   tickets            grain_delivery_tickets rows, re-shrunk to RMA net
//   shrinkPerPt, baseMoisture   the re-shrink (defaults 1.2 %/pt above 13.5%)
//   deliveredOverride  a typed delivered figure that REPLACES the tickets' RMA net (or null)
//   stored, seed, fed  bushels binned, kept for seed, fed on farm
//   appraisals         summarizeAppraisals(rows) — only its `bu` counts — or null when the
//                      include toggle is off / the crop has none
//   machine            { bu, acres } off the John Deere yield monitor, or null. SHOWN BESIDE
//                      production as a check and NEVER added to it.
//   combine            { bu, acres } — JD COMBINE bushels on GRAIN-insured fields only
//                      (claimAccounting.grainCombineOf: the Claims page's own "Combined" figure;
//                      a chopper pass is never in it), or null / absent. COUNTED as production
//                      when no measured record (tickets, a typed delivered figure, stored, seed,
//                      fed) is on file; where one is, the records take precedence — the same
//                      harvest weighed twice otherwise (productionSources' monitor rule) — and
//                      the combine figure is listed, not added.
//                      Kevin, 2026-11-03: "Combine is grain, choppers harvest silage as tons. So
//                      yes, if a field is insured as grain and we have JD yield data for grain
//                      bushels, use that." RAUBERDALE read its verdict off 971 bu while the page
//                      headline said 21,300 (20,329 of that is combine bushels).

// The combine must have covered this share of planted acres before its total is compared to
// production — the same 90% floor the production fill uses (Kevin, 2026-10-18).
export const MACHINE_COMPARE_MIN_COVERAGE = 0.90;
// Acre-scoped production covering at least this share of planted acres is the whole farm.
export const PROD_ACRE_TOL = 0.98;

const SOURCE_LABEL = Object.freeze({
  tickets: "Scale tickets (RMA net)",
  override: "Delivered (typed override)",
  stored: "Stored on farm",
  seed: "Saved for seed",
  fed: "Fed on farm",
  appraisal: "Appraised (bushels)",
  machine: "Machine yield (check only)",
  combine: "John Deere combine (grain-insured fields)",
});

export function computeHarvestClaim(cov, sources, harvestPx) {
  if (!sources || typeof sources !== "object") throw new Error("computeHarvestClaim: sources object is required");
  for (const k of ["tickets", "appraisals", "machine"]) {
    if (!(k in sources)) throw new Error(`computeHarvestClaim: source "${k}" not stated (pass null to leave it out)`);
  }
  const guarBu = cov?.guaranteeBu || 0;
  const projected = cov?.projectedPrice || 0;
  const tickets = sources.tickets || [];
  const shrink = sources.shrinkPerPt != null && sources.shrinkPerPt !== "" ? Number(sources.shrinkPerPt) : 1.2;
  const bm = sources.baseMoisture != null && sources.baseMoisture !== "" ? Number(sources.baseMoisture) : 13.5;
  const rmaNet = rmaNetFromTickets(tickets, shrink, bm);
  const override = sources.deliveredOverride != null && sources.deliveredOverride !== ""
    ? (Number(sources.deliveredOverride) || 0) : null;
  const delivered = override != null ? override : rmaNet;
  const stored = Number(sources.stored) || 0, seed = Number(sources.seed) || 0, fed = Number(sources.fed) || 0;
  const extra = stored + seed + fed;
  const apprBu = sources.appraisals ? (Number(sources.appraisals.bu) || 0) : 0;
  const combineIn = sources.combine && Number(sources.combine.bu) > 0 ? sources.combine : null;
  const combineCounted = !!combineIn && !(delivered + extra > 0);
  const combineBu = combineCounted ? Number(combineIn.bu) : 0;
  const combineAcres = combineCounted ? (Number(combineIn.acres) || 0) : 0;
  const production = delivered + extra + apprBu + combineBu;

  const lines = [
    { source: "tickets", label: SOURCE_LABEL.tickets, bu: rmaNet, counted: override == null,
      note: override != null ? "replaced by the typed delivered figure" : null },
  ];
  if (override != null) lines.push({ source: "override", label: SOURCE_LABEL.override, bu: override, counted: true, note: null });
  if (stored) lines.push({ source: "stored", label: SOURCE_LABEL.stored, bu: stored, counted: true, note: null });
  if (seed) lines.push({ source: "seed", label: SOURCE_LABEL.seed, bu: seed, counted: true, note: null });
  if (fed) lines.push({ source: "fed", label: SOURCE_LABEL.fed, bu: fed, counted: true, note: null });
  if (sources.appraisals) lines.push({ source: "appraisal", label: SOURCE_LABEL.appraisal, bu: apprBu, counted: true,
    acres: Number(sources.appraisals.buAcres ?? sources.appraisals.acres) || 0, note: null });
  if (combineIn) lines.push({ source: "combine", label: SOURCE_LABEL.combine, bu: Number(combineIn.bu), counted: combineCounted,
    acres: Number(combineIn.acres) || 0, note: combineCounted ? null : "records on file take precedence; not added" });

  const plans = cov?.plans || [];
  const basis = coverageBasis(plans).basis;
  const hpe = plans.length > 0 && plans.every(p => /HPE/.test(p));
  const s = harvestShortfall({ guarBu, production, projected, harvestPx, basis, hpe });

  // ── MACHINE DATA, BESIDE — NEVER IN ──────────────────────────────────────
  // Compared with what was MEASURED off the field (tickets or the typed delivered figure,
  // plus stored / seed / fed). Appraised bushels are left out of the comparison because an
  // appraisal stands for ground the combine did not harvest for grain.
  const machine = sources.machine && Number(sources.machine.bu) > 0 ? sources.machine : null;
  const measuredBu = delivered + extra;
  // ⚠ GRAIN PRODUCTION IS READ AGAINST GRAIN-INSURED ACRES ONLY (Kevin, 2026-11-03: "It all depends
  // on how the acres are insured"). Silage-insured (026) acres are settled in tons against their own
  // guarantee; with them in this denominator, a grain verdict waited for as long as any silage acre
  // lacked tons (audit 2026-11-03 #10). `grainPlantedAcres` comes from summarizeClaimCoverage; a
  // caller that does not supply it keeps the old basis.
  const planted = cov?.grainPlantedAcres != null ? Number(cov.grainPlantedAcres)
    : cov?.plantedAcres != null ? Number(cov.plantedAcres) : Number(cov?.acres) || 0;
  let machineCheck = { compared: false, machineBu: machine ? Number(machine.bu) : null,
    machineAcres: machine ? Number(machine.acres) || 0 : null, measuredBu, coverage: null,
    diffPct: null, flag: false, threshold: MACHINE_DISAGREE_PCT, reason: null };
  if (!machine) machineCheck.reason = "no machine yield on file";
  else if (!(measuredBu > 0)) machineCheck.reason = "no measured production to compare";
  else {
    const cover = planted > 0 ? (Number(machine.acres) || 0) / planted : null;
    machineCheck.coverage = cover;
    if (cover == null || cover < MACHINE_COMPARE_MIN_COVERAGE) {
      machineCheck.reason = "the combine covered too little of the planted acres to compare";
    } else {
      const d = machineDisagreement(measuredBu, machine.bu);
      machineCheck = { ...machineCheck, compared: true, diffPct: d.diffPct, flag: d.flag };
    }
  }
  if (machine) lines.push({ source: "machine", label: SOURCE_LABEL.machine, bu: Number(machine.bu), counted: false,
    acres: Number(machine.acres) || 0, note: "a check on production, never added to it" });

  // ── PRODUCTION THAT COVERS PART OF THE FARM CANNOT READ A CLAIM ────────────
  // An appraisal speaks for the acres it was taken on and nothing else. Where appraised
  // bushels are the WHOLE of what was counted and they cover less than the planted acres,
  // subtracting them from the whole-farm guarantee values every other acre at zero — the
  // ROGERS inversion recorded in ClaimCheckView. So the read is withheld ("not yet", never
  // "no claim"). The tolerance is the drill-in's own: two documents rounding.
  // ⚠ ONLY COUNTED SOURCES SAY HOW MUCH GROUND IS COVERED. Machine acres are not production,
  // so they cannot make appraisal-only production read as the whole farm. Counted COMBINE
  // bushels speak for the acres the combine measured, and no more: a combine-only farm that
  // has harvested 125 of 508 planted acres is "so far", never a 75% shortfall.
  const apprAcres = sources.appraisals ? (Number(sources.appraisals.buAcres ?? sources.appraisals.acres) || 0) : 0;
  const coveredAcres = apprAcres + combineAcres;
  // ⚠ A COUNTED COMBINE FIGURE THAT MEASURED NO ACRES COVERS NONE OF THE FARM. KAST (soybeans
  // 2026): a 0.02 ac sliver logged 1 bu, which read "UNDER, 0% of guarantee" — a claim flag off
  // one bushel. It is "so far" like any other part-farm figure, never a whole-farm reading.
  const partial = measuredBu <= 0 && production > 0 && planted > 0
    && (coveredAcres > 0 || combineCounted) && coveredAcres < planted * PROD_ACRE_TOL;
  const uncoveredAcres = partial ? Math.max(0, planted - coveredAcres) : 0;

  const hasTickets = tickets.length > 0;
  const hasData = guarBu > 0 && production > 0;
  // measured over/under: revenue basis when a harvest price exists, else bushel basis
  const measured = (!hasData || partial) ? null : (s.revToCount != null ? (s.revToCount < s.revGuar ? 'under' : 'over') : (production < guarBu ? 'under' : 'over'));
  return { guarBu, revGuar: s.revGuar, guarPrice: s.guarPrice, harvestUsed: s.harvestUsed, production,
    rmaNet, delivered, extra, appraisedBu: apprBu, combineBu, combineCounted, revToCount: s.revToCount, buShort: s.buShort,
    // A partial read has no estimate: "not yet", never a figure off zero-valued acres.
    revShort: s.revShort, estIndemnity: partial ? null : s.estIndemnity, hasTickets, measured, isYP: s.isYP, isHPE: hpe,
    basis, lines, machineCheck, partial, acreScopedAcres: coveredAcres, uncoveredAcres, plantedAcres: planted,
    absences: s.absences };
}

// The roster's adapter from a stored claim_check_inputs row. No arithmetic — it only names
// which column is which source, so the roster and the drill-in hand the engine the same keys.
export function harvestSourcesFromInputs(tickets, inputs, appraisals, machine, combine) {
  return {
    tickets: tickets || [],
    shrinkPerPt: inputs?.shrink_per_pt != null ? Number(inputs.shrink_per_pt) : null,
    baseMoisture: inputs?.base_moisture != null ? Number(inputs.base_moisture) : null,
    deliveredOverride: inputs?.delivered_override != null ? Number(inputs.delivered_override) : null,
    stored: inputs?.stored_bu, seed: inputs?.seed_bu, fed: inputs?.fed_bu,
    appraisals: appraisals || null,
    machine: machine || null,
    combine: combine || null,
  };
}

// ── IS THERE ENOUGH TO ESTIMATE WITH YET ─────────────────────────────────────
//
// Kevin: *"as soon as we have all the data for production or acres for PP"*. "As soon
// as" is a readiness question, and the two claim types become ready on different facts:
// a PP claim is ready when the acreage report is in, months before harvest; a
// production claim is not ready until the crop is off AND the harvest price is
// published.
//
// ⚠ A HARVEST PRICE THAT IS STILL AN ESTIMATE DOES NOT MAKE A CLAIM READY, and this is
// the one that would quietly corrupt the whole table. `computeHarvestClaim` happily
// accepts a running futures average as `harvestPx` — that is correct for the screener,
// which exists to say "open a claim or not" before RMA publishes. But an estimate frozen
// against a moving price is not a forecast of the carrier's arithmetic, it is a forecast
// of the market, and when the comparison later fires the delta says nothing about
// whether the AIP made a mistake. So readiness requires a FINAL price and the caller
// must say which it handed over.
export const READY = Object.freeze({
  READY: "READY",
  WAITING: "WAITING",
  NOT_ESTIMABLE: "NOT_ESTIMABLE",
});

export function estimateReadiness(cell = {}) {
  const kind = up(cell.claimType || cell.kind);
  const waiting = [], blocked = [];

  if (kind === "PP" || kind === "PREVENT_PLANT") {
    if (num(cell.ppAcres) == null) waiting.push("prevent-plant acres have not been reported");
    if (num(cell.share) == null) blocked.push("no insured share");
    // The AIP's own stated per-acre PP liability answers for all four components at once —
    // it is the carrier's own figure. Demanding them beside it would report a cell
    // the carrier has already priced as un-estimable.
    if (!(num(cell.ppLiabilityPerAcre) > 0)) {
      if (!(num(cell.approvedYield) > 0)) blocked.push("no approved yield on this unit");
      if (num(cell.coverageLevel) == null) blocked.push("no coverage level");
      if (num(cell.projectedPrice) == null) waiting.push("RMA has not published the projected price");
      if (num(cell.ppFactor) == null) blocked.push("no prevent-plant factor for this crop year");
    }
  } else if (kind === "PRODUCTION" || kind === "REVENUE" || kind === "HARVEST") {
    if (!(num(cell.guaranteeBu) > 0)) blocked.push("no bushel guarantee on this unit");
    if (num(cell.production) == null) waiting.push("production has not been reported");
    // Appraised bushels on part of the planted acres are a floor, not the farm's production.
    else if (cell.productionPartial === true) waiting.push("production so far covers only part of the planted acres");
    if (num(cell.harvestPrice) == null) waiting.push("RMA has not published the harvest price");
    // The screener's running average is explicitly not enough — see the note above.
    else if (cell.harvestPriceFinal !== true) waiting.push("the harvest price is still an estimate, not RMA's published final");
  } else {
    blocked.push("claim type not stated");
  }

  if (blocked.length) return { status: READY.NOT_ESTIMABLE, waiting, blocked };
  if (waiting.length) return { status: READY.WAITING, waiting, blocked };
  return { status: READY.READY, waiting: [], blocked: [] };
}

export function describeReadiness(r) {
  if (!r) return "";
  if (r.status === READY.READY) return "Ready to estimate";
  const all = [...(r.blocked || []), ...(r.waiting || [])];
  if (r.status === READY.NOT_ESTIMABLE) return "Cannot be estimated — " + all.join("; ");
  return "Waiting on " + (r.waiting || []).join("; ");
}
